import { FC, RefObject, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";

import { FileEntry, Form, useForm } from "capitalrx-components-2.0/lib/forms";
import { Link, useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading } from "capitalrx-components-2.0/lib/typography";

import { FileUpload } from "@/App/components/FileUpload";
import { ThemedButton } from "@/App/components/themed/Button";
import { StackOrShelf } from "@/App/components/StackOrShelf";

import { DirectMemberReimbursementAcknowledgeField } from "./DirectMemberReimbursementAcknowledgeField";
import { DirectMemberReimbursementContactInformationFields } from "./DirectMemberReimbursementContactInformationFields";
import { DirectMemberReimbursementUploadClaimEvidenceField } from "./DirectMemberReimbursementUploadClaimEvidenceField";
import { DirectMemberReimbursementSubmissionType } from "@/App/api/directMemberReimbursement";

const TRANSLATION_PATH = "DIRECT_MEMBER_REIMBURSEMENT.directMemberReimbursementUploadClaimForm";

export interface UploadClaimForm {
  readonly claim_form: FileEntry[];
  readonly claim_evidence_files: FileEntry[];
  readonly email_address: string;
  readonly phone_number: string;
  readonly acknowledge: boolean;
}

interface ClaimForm {
  readonly claim_form: FileEntry[];
}

interface ClaimEvidenceForm {
  readonly claim_evidence_files: FileEntry[];
}

interface ContactInformationForm {
  readonly email_address: string;
  readonly phone_number: string;
}

interface AcknowledgeForm {
  readonly acknowledge: boolean;
}

interface DirectMemberReimbursementUploadClaimFormProps {
  claimType: DirectMemberReimbursementSubmissionType;
  readonly onSubmitCallbackHandler: (values: UploadClaimForm) => Promise<void>;
}

export const DirectMemberReimbursementUploadClaimForm: FC<DirectMemberReimbursementUploadClaimFormProps> = ({ claimType, onSubmitCallbackHandler }) => {
  const { t: translate, i18n } = useTranslation();
  const claimUploadAnchorRef = useRef<HTMLSpanElement>(null);
  const evidenceUploadAnchorRef = useRef<HTMLSpanElement>(null);
  const contactAnchorRef = useRef<HTMLAnchorElement>(null);
  const acknowledgeAnchorRef = useRef<HTMLSpanElement>(null);
  const showAlert = useAlert();

  const isEmulating = !!sessionStorage.getItem("isEmulation");

  const claimForm = useForm<ClaimForm>();
  const evidenceForm = useForm<ClaimEvidenceForm>();
  const contactForm = useForm<ContactInformationForm>({ shouldFocusError: false });
  const acknowledgeForm = useForm<AcknowledgeForm>({ shouldFocusError: false });

  const { emailColumns, phoneColumns } = useResponsive({
    emailColumns: {
      desktop: 3,
      tablet: 6,
      mobile: 12,
    },
    phoneColumns: {
      desktop: 2,
      tablet: 4,
      mobile: 12,
    },
  });

  const handleSubmitAll = async () => {
    const results: boolean[] = await Promise.all([
      acknowledgeForm.trigger(),
      contactForm.trigger(),
      claimForm.trigger(),
      claimType === "pharmacy" && evidenceForm ? evidenceForm.trigger() : true,
    ]);

    const [acknowledgeValid, contactValid, claimValid, evidenceValid] = results;
    const allValid = acknowledgeValid && contactValid && claimValid && evidenceValid;

    if (!allValid) {
      const sectionAnchors: Array<[boolean, RefObject<HTMLElement>]> = [
        [claimValid, claimUploadAnchorRef],
        [evidenceValid, evidenceUploadAnchorRef],
        [contactValid, contactAnchorRef],
        [acknowledgeValid, acknowledgeAnchorRef],
      ];

      for (const [valid, ref] of sectionAnchors) {
        if (!valid && ref?.current) {
          showAlert({ type: "error", style: "toast", message: translate(`DIRECT_MEMBER_REIMBURSEMENT.directMemberReimbursementClaimDetailsForm.errorSubmittingDMR`) });
          ref.current.scrollIntoView({ block: "center" });
          break;
        }
      }

      return;
    }

    const composedData: UploadClaimForm = {
      claim_form: claimForm.getValues ? claimForm.getValues().claim_form ?? [] : [],
      claim_evidence_files: claimType === "pharmacy" ? evidenceForm?.getValues?.().claim_evidence_files ?? [] : [],
      email_address: contactForm.getValues ? contactForm.getValues().email_address ?? "" : "",
      phone_number: contactForm.getValues ? contactForm.getValues().phone_number ?? "" : "",
      acknowledge: acknowledgeForm.getValues ? !!acknowledgeForm.getValues().acknowledge : false,
    };

    onSubmitCallbackHandler(composedData);
  };

  return (
    <Stack spacing="large">
      <Stack spacing="medium">
        {claimType === "pharmacy" && (
          <>
            <Heading>{translate(`${TRANSLATION_PATH}.uploadAClaimForm`)}</Heading>
            <Body>
              <ol style={{ marginTop: 0, marginBottom: 0, paddingLeft: "1rem" }}>
                <li>
                  <Trans
                    i18n={i18n}
                    i18nKey={`${TRANSLATION_PATH}.downloadForm`}
                    components={{
                      DownloadFormLink: (
                        <Link
                          to={
                            "https://cdn.prod.website-files.com/68cc627e5282a3e381242e28/68cc762be3d35991926eb719_Prescription-Drug-Claim-Form_2024_Fillable-1.pdf"
                          }
                        >
                          {translate(`${TRANSLATION_PATH}.prescriptionDrugClaimForm`)}
                        </Link>
                      ),
                    }}
                  />
                </li>
                <li>{translate(`${TRANSLATION_PATH}.fillFormInstructions`)}</li>
                <li>{translate(`${TRANSLATION_PATH}.saveOrScanForm`)}</li>
                <li>{translate(`${TRANSLATION_PATH}.uploadTheCompletedForm`)}</li>
              </ol>
            </Body>
          </>
        )}
        <Form form={claimForm}>
          <span ref={claimUploadAnchorRef}>
            <FileUpload.Field
              label={claimType === "pharmacy" ? translate(`${TRANSLATION_PATH}.selectFile`) : translate(`${TRANSLATION_PATH}.selectFiles`)}
              name="claim_form"
              allowedExtensions={[".pdf", ".png", ".jpg", ".heic", ".docx"]}
              maxSize={4500000}
              allowMultiple={claimType === "medical"}
              required
            />
          </span>
        </Form>
      </Stack>
      {claimType === "pharmacy" && (
        <Form form={evidenceForm}>
          <span ref={evidenceUploadAnchorRef}>
            <DirectMemberReimbursementUploadClaimEvidenceField
              label={translate(`${TRANSLATION_PATH}.selectFiles`)}
              name="claim_evidence_files"
            />
          </span>
        </Form>
      )}
      <Form form={contactForm}>
        <span ref={contactAnchorRef}>
          <DirectMemberReimbursementContactInformationFields
            smallColumns={phoneColumns}
            mediumColumns={emailColumns}
            emailAddressFieldName="email_address"
            phoneNumberFieldName="phone_number"
          />
        </span>
      </Form>
      <Form form={acknowledgeForm}>
        <span ref={acknowledgeAnchorRef}>
          <DirectMemberReimbursementAcknowledgeField fieldName="acknowledge" />
        </span>
      </Form>
      <StackOrShelf>
        <ThemedButton appearance="primary" onClick={handleSubmitAll} disabled={isEmulating}>
          {translate(`${TRANSLATION_PATH}.submit`)}
        </ThemedButton>
        <Shelf.Spacer />
      </StackOrShelf>
    </Stack>
  );
};
